import moment from 'moment';
import { EngineStatusName } from '../Engine/constants';
import { getEngineStatusColor, isValidEngineStatusName } from '../Engine/utils';

export type EngineStatusLogRecord = { startDate: string; state: EngineStatusName };

export interface EngineStatusApiResponse {
  statuses: Array<{ status: string | null; startTime: string | null; endTime?: string | null }>;
}

export interface EngineStateChangePoint {
  x: number;
  startDate: string;
  state: EngineStatusName | null;
  color: string;
}

export function isValidApiResponse(response: unknown): response is EngineStatusApiResponse {
  if (!response || typeof response !== 'object') {
    return false;
  }

  const r = response as Record<string, unknown>;

  if (!Array.isArray(r.statuses)) {
    return false;
  }

  return r.statuses.every(
    item =>
      item &&
      typeof item === 'object' &&
      (typeof item.status === 'string' || item.status === null) &&
      (typeof item.startTime === 'string' || item.startTime === null) &&
      (item.endTime === undefined || typeof item.endTime === 'string' || item.endTime === null),
  );
}

export function isValidDateString(dateStr: string | null): dateStr is string {
  if (!dateStr || typeof dateStr !== 'string') {
    return false;
  }

  const momentDate = moment(dateStr);
  return momentDate.isValid();
}

export function normalizeResponse(response: unknown): EngineStatusLogRecord[] {
  if (!isValidApiResponse(response)) {
    return [];
  }

  const result: EngineStatusLogRecord[] = [];
  let previousEndTime: string | null = null;

  for (const item of response.statuses) {
    const hasValidStartTime = isValidDateString(item.startTime);
    const hasValidEndTime = item.endTime !== undefined && isValidDateString(item.endTime);

    const endTime = item.endTime;

    if (hasValidStartTime) {
      result.push({
        startDate: item.startTime,
        state: isValidEngineStatusName(item.status) ? item.status : EngineStatusName.Unavailable,
      });
      previousEndTime = hasValidEndTime && endTime ? endTime : null;
    } else if (previousEndTime && hasValidEndTime && endTime) {
      result.push({
        startDate: previousEndTime,
        state: EngineStatusName.Unavailable,
      });
      previousEndTime = endTime;
    }
  }

  return result;
}

export function getEngineStateChangePoints(
  engineStatusLogs: Array<{ startDate: string; newState: string }> | null,
  periodStartDate: string | null,
  periodEndDate: string | null,
): EngineStateChangePoint[] {
  if (!engineStatusLogs?.length || !periodStartDate || !periodEndDate) {
    return [];
  }

  const formattedPeriodStartMoment = moment(periodStartDate).utc();
  const formattedPeriodEndMoment = moment(periodEndDate).utc();

  if (!formattedPeriodStartMoment.isValid() || !formattedPeriodEndMoment.isValid()) {
    return [];
  }

  const formattedPeriodStartDate = formattedPeriodStartMoment.valueOf();
  const formattedPeriodEndDate = formattedPeriodEndMoment.valueOf();

  return engineStatusLogs
    .map((log, index) => {
      if (!log?.startDate) return null;

      const logMoment = moment(log.startDate).utc();

      if (!logMoment.isValid()) {
        return null;
      }

      const logTime = logMoment.valueOf();

      let changeTime: number;
      if (index === 0) {
        changeTime = formattedPeriodStartDate;
      } else {
        if (logTime < formattedPeriodStartDate || logTime > formattedPeriodEndDate) return null;
        changeTime = logTime;
      }

      const state: EngineStatusName | null = isValidEngineStatusName(log?.newState) ? log.newState : null;

      return {
        x: changeTime,
        startDate: log.startDate,
        state,
        color: getEngineStatusColor(state),
      };
    })
    .filter(Boolean) as EngineStateChangePoint[];
}
